/*
 |-----------------------------------------------------------------------------
 | components/organisms/Feature/Feature.tsx
 | v1.0.0
 |-----------------------------------------------------------------------------
 |
 | Some description...
 |
 */

import { FC } from 'react';

import { filter, get, isEmpty } from 'lodash';
import Head from 'next/head';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
// import { ray } from 'node-ray/web';

import Button from '@/atoms/Button';
import Picture from '@/atoms/Picture';
import { articlesSelector } from '@/features/articles';
import { fallback } from '@/utils/fallback';
import { transform } from '@/utils/transform';
import { useAppSelector } from '@/app/hooks';

import * as IFeature from './types';
import styles from './Feature.module.scss';

const Alert = dynamic(() => import('@/molecules/Alert'));

const Feature: FC<IFeature.IProps> = ({ title, variant }) => {
	// ray('Debug organism Feature:', {
	// 	title: title,
	// 	variant: variant,
	// }).purple();

	if (isEmpty(variant)) {
		return (
			<Alert
				isDismissible
				text="No collection has been selected"
				variant="warning"
			/>
		);
	}

	let classes, content, transforms;
	switch (variant) {
		case 'article':
			/* eslint-disable react-hooks/rules-of-hooks */
			const articles = useAppSelector(articlesSelector);

			const featuredArticles = filter(articles, ({ node }) => {
				return get(node, 'feature');
			});

			const { node: featuredArticle } = featuredArticles[0];

			const image = fallback(
				get(featuredArticle, ['card', 'image', 'src']),
				get(featuredArticle, ['image', 'src'])
			);

			classes = classNames(get(styles, 'article'));

			transforms = {
				default: transform(image, 'f_auto,c_fill,h_509,w_814'),
				tabletL: transform(
					image,
					'f_auto,c_thumb,g_face,z_0.3,h_509,w_407'
				),
				tabletP: transform(image, 'f_auto,c_fill,h_509,w_814'),
				mobileL: transform(image, 'f_auto,c_fill,h_509,w_814'),
				mobileS: transform(image, 'f_auto,c_fill,h_509,w_814'),
			};

			content = (
				<div className={get(styles, 'container')}>
					<div className={get(styles, 'wrapper')}>
						<header className={get(styles, 'header')}>
							<h2 className={get(styles, 'title')}>
								{title || 'Feature'}
							</h2>
						</header>

						<div className={get(styles, 'main')}>
							<h3
								className={get(styles, 'documentTitle')}
								itemProp="headline"
							>
								{fallback(
									get(featuredArticle, ['card', 'title']),
									get(featuredArticle, 'title')
								)}
							</h3>

							<p
								className={get(styles, 'documentText')}
								itemProp="description"
							>
								{get(featuredArticle, ['card', 'text'])}
							</p>

							<Button
								className="action"
								label={get(featuredArticle, ['card', 'action'])}
								path={`/news/${get(featuredArticle, [
									'_sys',
									'filename',
								])}`}
								variant="secondary"
							/>
						</div>
					</div>
					{image && (
						<Picture
							alt={get(image, 'alt')}
							lazyLoad
							schema="thumbnail"
							src={transforms}
						/>
					)}
				</div>
			);
			break;
		default:
			content = (
				<Alert
					isDismissible
					text={`The collection ${variant} could not be found`}
					variant="warning"
				/>
			);
	}

	return (
		<>
			<Head>
				<link
					rel="preload"
					as="image"
					imageSrcSet={`
						${get(transforms, 'default')} 1200w,
						${get(transforms, 'tabletL')} 1024w,
						${get(transforms, 'tabletP')} 768w,
						${get(transforms, 'mobileL')} 414w,
						${get(transforms, 'mobileS')} 375w
					`}
				/>
			</Head>
			<header className={classes}>{content}</header>
		</>
	);
};

export default Feature;
